import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren
} from "react";
import { Platform } from "react-native";

const DEMO_MODE_KEY = "recital-master.demo-mode.v1";

// On web the backing store is localStorage, which is synchronous. Hydrate in
// the initial render: browser history traversals can remount this provider,
// and an async hydrate would report "no demo session" for a frame — long
// enough for the root curtain's session key to blip null and re-arm.
function readDemoModeSync(): boolean | null {
  if (Platform.OS !== "web") return null;
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(DEMO_MODE_KEY) === "1";
  } catch {
    return null;
  }
}

type DemoModeState = {
  active: boolean;
  loading: boolean;
  enterDemo: () => Promise<void>;
  exitDemo: () => Promise<void>;
};

const DemoModeContext = createContext<DemoModeState | null>(null);

export function DemoModeProvider({ children }: PropsWithChildren) {
  const syncValue = readDemoModeSync();
  const [active, setActive] = useState(syncValue ?? false);
  const [loading, setLoading] = useState(syncValue == null);

  useEffect(() => {
    if (syncValue != null) return undefined;
    let cancelled = false;
    AsyncStorage.getItem(DEMO_MODE_KEY)
      .then((value) => {
        if (!cancelled) setActive(value === "1");
      })
      .catch(() => {
        if (!cancelled) setActive(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enterDemo = useCallback(async () => {
    await AsyncStorage.setItem(DEMO_MODE_KEY, "1");
    setActive(true);
  }, []);

  const exitDemo = useCallback(async () => {
    await AsyncStorage.removeItem(DEMO_MODE_KEY);
    setActive(false);
  }, []);

  const value = useMemo(
    () => ({ active, loading, enterDemo, exitDemo }),
    [active, enterDemo, exitDemo, loading]
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export function useDemoMode(): DemoModeState {
  const context = useContext(DemoModeContext);
  if (!context) throw new Error("useDemoMode requires DemoModeProvider");
  return context;
}
